// Angular
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import vi from '@angular/common/locales/vi';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

// Third-party
import { IconDefinition } from '@ant-design/icons-angular';
// Only the icons actually rendered anywhere in the app, plus the ones ng-zorro's own
// components render internally (clear buttons, pagination arrows, select caret, spinner).
// Importing the namespace and mapping Object.keys() over it pulled in all 789 icon
// definitions eagerly and defeated tree-shaking, since every key was referenced.
// Adding a new nz-icon nzType means adding its export here, or it renders blank with a
// console warning from NzIconService.
import {
    ArrowRightOutline,
    BorderOutline,
    CheckCircleFill,
    CheckCircleOutline,
    CheckOutline,
    CheckSquareOutline,
    CloseCircleFill,
    CloseCircleOutline,
    CloseOutline,
    DeleteOutline,
    DoubleLeftOutline,
    DoubleRightOutline,
    DownloadOutline,
    DownOutline,
    EditOutline,
    EllipsisOutline,
    ExclamationCircleFill,
    ExclamationOutline,
    EyeInvisibleOutline,
    EyeOutline,
    FilePdfOutline,
    FileSearchOutline,
    InboxOutline,
    InfoCircleOutline,
    LeftOutline,
    LoadingOutline,
    MinusOutline,
    PlusOutline,
    QuestionCircleFill,
    QuestionOutline,
    RightOutline,
    RobotOutline,
    SearchOutline,
    SyncOutline,
    ThunderboltOutline,
    UploadOutline,
    UserOutline,
    WarningOutline,
} from '@ant-design/icons-angular/icons';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import { QuillModule } from 'ngx-quill';
import { quillModules } from './core/config/quill.config';
import { TranslateModule } from '@ngx-translate/core';
// App modules
import { AppRoutingModule } from './app-routing.module';
import { ModalsModule } from './shared/modals/modals.module';
import { SharedModule } from './shared/shared.module';

// App helpers, providers, etc.
import { EnvServiceProvider } from './core/helpers/env.service.provider';
import { provideAppTranslateHttpLoader } from './core/config/translate-loader.factory';

// Components
import { AppComponent } from './app.component';
import { FooterComponent } from './layouts/footer/footer.component';
import { HeaderComponent } from './layouts/header/header.component';
import { NotificationComponent } from './layouts/header/notification/notification.component';
import { HomeComponent } from './views/home/home.component';
import { NzModalModule } from 'ng-zorro-antd/modal';

registerLocaleData(en);
registerLocaleData(vi);

const icons: IconDefinition[] = [
    ArrowRightOutline,
    BorderOutline,
    CheckCircleFill,
    CheckCircleOutline,
    CheckOutline,
    CheckSquareOutline,
    CloseCircleFill,
    CloseCircleOutline,
    CloseOutline,
    DeleteOutline,
    DoubleLeftOutline,
    DoubleRightOutline,
    DownloadOutline,
    DownOutline,
    EditOutline,
    EllipsisOutline,
    ExclamationCircleFill,
    ExclamationOutline,
    EyeInvisibleOutline,
    EyeOutline,
    FilePdfOutline,
    FileSearchOutline,
    InboxOutline,
    InfoCircleOutline,
    LeftOutline,
    LoadingOutline,
    MinusOutline,
    PlusOutline,
    QuestionCircleFill,
    QuestionOutline,
    RightOutline,
    RobotOutline,
    SearchOutline,
    SyncOutline,
    ThunderboltOutline,
    UploadOutline,
    UserOutline,
    WarningOutline,
];

@NgModule({
    declarations: [
        AppComponent,
        FooterComponent,
        HeaderComponent,
        HomeComponent,
    ],
    imports: [
        NzModalModule,
        NotificationComponent,
        BrowserModule,
        HttpClientModule,
        AppRoutingModule,
        SharedModule,
        ModalsModule,
        QuillModule.forRoot({
            modules: quillModules,
            placeholder: '',
        }),
        TranslateModule.forRoot({
            fallbackLang: 'en',
            loader: provideAppTranslateHttpLoader(),
        }),
    ],
    providers: [
        EnvServiceProvider,
        provideNzI18n(en_US),
        provideAnimationsAsync(),
        { provide: NZ_ICONS, useValue: icons },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
